import {FC, useEffect, useState, useMemo} from 'react'
import {useNavigate} from 'react-router-dom'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Loading} from '../../../components/Loading/Loading'
import {GET, PUT, POST} from '../../../services/api'
import Swal from 'sweetalert2'
import {ModalWrapper} from '../../../components/Modals/General/ModalWrapper/ModalWrapper'
import useModals from '../../../hooks/useModals'
import {secondsToHour} from '../../../utils/formater'
import {TableSuggestionModal} from '../../../components/TableSuggestionModal'
import {Armchair} from 'lucide-react'

// Função auxiliar para formatar data sem problemas de timezone
const formatDateBR = (dateString: string): string => {
  // Extrai apenas a parte da data (YYYY-MM-DD) ignorando timezone
  const datePart = dateString.split('T')[0]
  const [year, month, day] = datePart.split('-')
  return `${day}/${month}/${year}`
}

// Função para calcular preço do ambiente baseado na quantidade de pessoas
const calculateEnvironmentPrice = (env: EnvironmentType, people: number): number => {
  // 1. Verifica se tem preço por grupo
  if (env.priceByGroup && env.priceByGroup.length > 0) {
    const group = env.priceByGroup.find((g) => people >= g.minPeople && people <= g.maxPeople)
    if (group) return group.price / 100 // Converte de centavos para reais
  }

  // 2. Preço por ambiente (valor fixo)
  if (env.pricePerEnvironment && env.pricePerEnvironment > 0) {
    return env.pricePerEnvironment / 100 // Converte de centavos para reais
  }

  // 3. Preço por pessoa
  if (env.pricePerSeat && env.pricePerSeat > 0) {
    return (env.pricePerSeat / 100) * people // Converte de centavos para reais
  }

  // 4. Preço por mesa
  if (env.pricePerTable && env.pricePerTable > 0 && env.seatsPerTable && env.seatsPerTable > 0) {
    const tablesNeeded = Math.ceil(people / env.seatsPerTable)
    return (env.pricePerTable / 100) * tablesNeeded // Converte de centavos para reais
  }

  return 0
}

// Função para verificar se uma reserva é futura (ainda não aconteceu)
const isBookingInFuture = (booking: BookingType): boolean => {
  const bookingDate = booking.date.split('T')[0] // YYYY-MM-DD
  const bookingHour = booking.bookingHour?.hour || '00:00'
  const bookingDateTime = new Date(`${bookingDate}T${bookingHour}:00`)
  const now = new Date()
  return bookingDateTime > now
}

type BookingType = {
  id: number
  date: string
  expiresIn: string
  people: number
  name: string
  email: string
  phone: string
  environment: string
  bookingEnvironmentId: number
  alarm: boolean
  secondsToExpires: number
  bookingConfigId: string
  bookingHour?: {hour: string}
  finalized?: string
  customerArrived?: number
  payment?: number | null
}

type EnvironmentType = {
  id: number
  name: string
  seatsAvailable: number
  tables: number
  pricePerSeat?: number | null
  pricePerEnvironment?: number | null
  pricePerTable?: number | null
  seatsPerTable?: number | null
  priceByGroup?: Array<{minPeople: number; maxPeople: number; price: number}> | null
}

type TableCombination = {
  tableIds: number[]
  tableNumbers: string[]
  totalCapacity: number
  wastePercentage: number
  allocationMethod: string
}

type SuggestionData = {
  hasAvailableTables: boolean
  suggestions: TableCombination[]
  environment: {
    id: number
    name: string
  }
  booking: {
    id: number
    name: string
    people: number
    hour: string
    date: string
  }
}

type CalendarDay = {
  date: Date
  dateString: string
  bookingsCount: number
  isToday: boolean
  isCurrentMonth: boolean
}

const BookingManagerDayContent: FC = () => {
  const navigate = useNavigate()
  const {showWrapperModal, closeWrapperModal} = useModals()

  // Estados principais
  const [loading, setLoading] = useState(false)
  const [bookings, setBookings] = useState<BookingType[]>([])
  const [allBookings, setAllBookings] = useState<BookingType[]>([])
  const [bookingSelected, setBookingSelected] = useState<BookingType | null>(null)
  const [option, setOption] = useState('')
  const [bookingsWithAllocations, setBookingsWithAllocations] = useState<Set<number>>(new Set())

  // Estados de filtros
  const [environments, setEnvironments] = useState<EnvironmentType[]>([])
  const [selectedEnvironment, setSelectedEnvironment] = useState<number | 'all'>('all')
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0])
  const [selectedHour, setSelectedHour] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [availableHours, setAvailableHours] = useState<string[]>([])
  const [availableDates, setAvailableDates] = useState<string[]>([])
  const [isRestoringFromStorage, setIsRestoringFromStorage] = useState<boolean>(true) // Flag para impedir sobrescrita durante restauração

  // Estados de visualização
  const [viewMode, setViewMode] = useState<'list' | 'month' | 'day'>('list')
  const [currentMonth, setCurrentMonth] = useState(new Date())

  // Estados de sugestão de mesa
  const [showTableSuggestionModal, setShowTableSuggestionModal] = useState(false)
  const [tableSuggestions, setTableSuggestions] = useState<SuggestionData | null>(null)
  const [bookingForAllocation, setBookingForAllocation] = useState<BookingType | null>(null)
  const [expandSuggestionsAccordion, setExpandSuggestionsAccordion] = useState(false)
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number | null>(null)

  const [, setBookingConfigId] = useState<number | null>(null)
  const [expiryTimeLimit, setExpiryTimeLimit] = useState<number>(0)
  const [enableTableManagement, setEnableTableManagement] = useState<boolean>(false)
  const [liveTimer, setLiveTimer] = useState<number>(0)
  const [allocatedTables, setAllocatedTables] = useState<any[]>([])

  // Função para buscar mesas alocadas para uma reserva
  const fetchAllocatedTables = async (bookingId: number) => {
    try {
      console.log('🔍 Buscando mesas alocadas para booking:', bookingId)
      const res = await GET(`/queueReservation/table-management/bookings/${bookingId}/allocations`)
      console.log('📥 Resposta fetchAllocatedTables:', res)
      
      if (res && res.data && Array.isArray(res.data)) {
        console.log('✅ Mesas alocadas encontradas:', res.data.length, res.data)
        setAllocatedTables(res.data)
      } else if (res && res.success === false) {
        console.warn('⚠️ Resposta sem sucesso:', res)
        setAllocatedTables([])
      } else {
        console.log('⚠️ Resposta em formato inesperado, tentando acessar data:', res)
        setAllocatedTables([])
      }
    } catch (e) {
      console.error('❌ Erro ao buscar mesas alocadas:', e)
      setAllocatedTables([])
    }
  }

  // Função para atualizar status da mesa
  const updateTableStatus = async (tableAllocationId: number, newStatus: string) => {
    try {
      const res = await PUT(`/queueReservation/tableAllocation/${tableAllocationId}`, {
        status: newStatus,
      })
      if (res && res.success) {
        // Atualizar lista local
        setAllocatedTables(
          allocatedTables.map((table) =>
            table.id === tableAllocationId ? {...table, status: newStatus} : table
          )
        )
        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: `Mesa atualizada para ${newStatus}`,
          showConfirmButton: false,
          timer: 1500,
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Falha ao atualizar status da mesa',
        })
      }
    } catch (e) {
      console.log('Erro ao atualizar mesa:', e)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Falha ao atualizar status da mesa',
      })
    }
  }

  // Tipo para filtros do localStorage
  type StoredFilters = {
    selectedEnvironment: number | 'all'
    selectedDate: string
    selectedHour: string
  }

  const STORAGE_KEY = 'booking-manager-day-filters'

  // Salva filtros no localStorage
  const saveFiltersToStorage = (filters: StoredFilters) => {
    try {
      const json = JSON.stringify(filters)
      localStorage.setItem(STORAGE_KEY, json)
    } catch (error) {
      console.warn('⚠️ Erro ao salvar filtros no localStorage:', error)
    }
  }

  // Carrega filtros do localStorage
  const loadFiltersFromStorage = (): StoredFilters | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        return parsed
      }
      return null
    } catch (error) {
      console.warn('⚠️ Erro ao carregar filtros do localStorage:', error)
      return null
    }
  }

  // Função para obter configuração de reservas
  const getBookingConfig = async () => {
    try {
      const res = await GET('/queueReservation/bookingConfig')
      if (res.success && res.data && res.data[0]) {
        setBookingConfigId(res.data[0].id)
        setExpiryTimeLimit(res.data[0].expiryTime || 0)
        setEnableTableManagement(res.data[0].enableTableManagement === 1)

        // Busca ambientes
        if (res.data[0].environment && res.data[0].environment.length > 0) {
          setEnvironments(res.data[0].environment)
        }

        return res.data[0]
      }
      return null
    } catch (e) {
      console.log('Erro ao buscar configuração:', e)
      return null
    }
  }

  // Função para buscar todas as reservas de um período
  const getAllBookings = async (configId: number, envList: EnvironmentType[]) => {
    try {
      const res = await GET(`/queueReservation/booking/${configId}`)
      if (res.data && res.data.length > 0) {
        const processedBookings = res.data.map((bk: BookingType) => {
          // Calcula segundos até expirar baseado na data/hora da reserva
          let seconds = 0
          if (bk.expiresIn) {
            // expiresIn vem como "HH:MM", converte para segundos
            seconds =
              parseInt(bk.expiresIn.split(':')[0]) * 3600 +
              parseInt(bk.expiresIn.split(':')[1]) * 60
          } else {
            // Se não tiver expiresIn, calcula baseado na data/hora da reserva
            const bookingDate = bk.date.split('T')[0]
            const bookingHour = bk.bookingHour?.hour || '00:00'
            const bookingDateTime = new Date(`${bookingDate}T${bookingHour}:00`)
            const now = new Date()
            const diffMs = bookingDateTime.getTime() - now.getTime()
            const diffSeconds = Math.floor(diffMs / 1000)
            seconds = diffSeconds > 0 ? diffSeconds : 0
          }

          bk.secondsToExpires = seconds
          bk.alarm = seconds / 60 < expiryTimeLimit && seconds > 0

          // Adiciona nome do ambiente usando a lista passada como parâmetro
          const env = envList.find((e) => e.id === bk.bookingEnvironmentId)
          bk.environment = env ? env.name : 'N/A'

          return bk
        })

        setAllBookings(processedBookings)

        // Extrai datas disponíveis (com validação de data inválida)
        const dateSet = new Set<string>()
        processedBookings.forEach((bk: BookingType) => {
          try {
            const date = new Date(bk.date)
            if (!isNaN(date.getTime())) {
              dateSet.add(date.toISOString().split('T')[0])
            }
          } catch (e) {
            console.warn('Data inválida:', bk.date)
          }
        })
        const dates = Array.from(dateSet).sort()
        setAvailableDates(dates)

        // Se há datas e a data selecionada não está na lista, seleciona a primeira ou hoje
        // (MAS não sobrescreve se estamos restaurando do localStorage)
        if (dates.length > 0 && !isRestoringFromStorage) {
          const today = new Date().toISOString().split('T')[0]
          if (dates.includes(today)) {
            setSelectedDate(today)
          } else if (!dates.includes(selectedDate)) {
            setSelectedDate(dates[0])
          }
        }

        // Extrai horários disponíveis
        const hourSet = new Set<string>()
        processedBookings.forEach((bk: BookingType) => {
          if (bk.bookingHour?.hour) {
            hourSet.add(bk.bookingHour.hour)
          }
        })
        const hours = Array.from(hourSet).sort()
        setAvailableHours(hours)

        return processedBookings
      }
      return []
    } catch (e) {
      console.log('Erro ao buscar reservas:', e)
      return []
    }
  }

  // Filtra reservas baseado nos filtros selecionados
  const filteredBookings = useMemo(() => {
    let filtered = [...allBookings]

    // Filtro por ambiente
    if (selectedEnvironment !== 'all') {
      const envId = Number(selectedEnvironment)
      filtered = filtered.filter((bk) => Number(bk.bookingEnvironmentId) === envId)
    }

    // Filtro por data
    if (selectedDate) {
      filtered = filtered.filter((bk) => {
        // Compara diretamente as strings de data no formato YYYY-MM-DD
        // Evita problemas de timezone ao criar objetos Date
        const bookingDate = bk.date.split('T')[0] // Pega apenas a parte da data (YYYY-MM-DD)
        return bookingDate === selectedDate
      })
    }

    // Filtro por horário
    if (selectedHour !== 'all') {
      filtered = filtered.filter((bk) => {
        // Padroniza o horário do booking para comparação (HH format)
        const bookingHourStr = String(bk.bookingHour?.hour).padStart(2, '0')
        return bookingHourStr === selectedHour
      })
    }

    // Filtro por nome ou telefone
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim()
      filtered = filtered.filter((bk) => {
        const name = bk.name?.toLowerCase() || ''
        const phone = bk.phone?.toLowerCase() || ''
        return name.includes(searchLower) || phone.includes(searchLower)
      })
    }

    return filtered
  }, [allBookings, selectedEnvironment, selectedDate, selectedHour, searchTerm])

  // Estatísticas calculadas
  const statistics = useMemo(() => {
    const total = filteredBookings.length
    const confirmed = filteredBookings.filter(
      (bk) => bk.finalized === 'confirmed' || bk.customerArrived === 1
    ).length
    const pending = filteredBookings.filter(
      (bk) => !bk.finalized || bk.finalized === 'pending'
    ).length
    const canceled = filteredBookings.filter((bk) => bk.finalized === 'canceled').length

    // Calcula capacidade total dos ambientes filtrados
    let totalCapacity = 0
    if (selectedEnvironment === 'all') {
      totalCapacity = environments.reduce((sum, env) => sum + env.seatsAvailable, 0)
    } else {
      const env = environments.find((e) => e.id === selectedEnvironment)
      totalCapacity = env ? env.seatsAvailable : 0
    }

    // Calcula pessoas reservadas
    const totalPeople = filteredBookings.reduce((sum, bk) => sum + bk.people, 0)
    const occupancyRate = totalCapacity > 0 ? (totalPeople / totalCapacity) * 100 : 0

    return {
      total,
      confirmed,
      pending,
      canceled,
      totalPeople,
      totalCapacity,
      occupancyRate: Math.min(occupancyRate, 100),
      availableSeats: Math.max(totalCapacity - totalPeople, 0),
    }
  }, [filteredBookings, environments, selectedEnvironment])

  // Calcula horários disponíveis com base nos filtros atuais (Ambiente + Data)
  const dynamicAvailableHours = useMemo(() => {
    let bookingsToConsider = allBookings

    // Se há ambiente selecionado, filtra por ambiente
    if (selectedEnvironment !== 'all') {
      const envId = Number(selectedEnvironment)
      bookingsToConsider = bookingsToConsider.filter(
        (bk) => Number(bk.bookingEnvironmentId) === envId
      )
    }

    // Se há data selecionada, filtra por data
    if (selectedDate) {
      bookingsToConsider = bookingsToConsider.filter((bk) => {
        const bookingDate = bk.date.split('T')[0]
        return bookingDate === selectedDate
      })
    }

    // Extrai horários únicos dos bookings filtrados
    const hourSet = new Set<string | number>()
    bookingsToConsider.forEach((bk) => {
      if (bk.bookingHour?.hour !== undefined && bk.bookingHour?.hour !== null) {
        // Converte para string para padronizar
        hourSet.add(String(bk.bookingHour.hour))
      }
    })

    // Retorna horários ordenados como strings (para manter compatibilidade com selectedHour)
    return Array.from(hourSet)
      .map((h) => String(h).padStart(2, '0'))
      .sort()
  }, [allBookings, selectedEnvironment, selectedDate])

  // Gera calendário do mês
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()

    const firstDay = new Date(year, month, 1)
    // const lastDay = new Date(year, month + 1, 0)
    const startDate = new Date(firstDay)
    startDate.setDate(startDate.getDate() - firstDay.getDay())

    const days: CalendarDay[] = []
    const today = new Date().toISOString().split('T')[0]

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate)
      date.setDate(date.getDate() + i)

      const dateString = date.toISOString().split('T')[0]
      const bookingsCount = allBookings.filter((bk) => {
        try {
          const bkDate = new Date(bk.date)
          if (!isNaN(bkDate.getTime())) {
            return bkDate.toISOString().split('T')[0] === dateString
          }
        } catch (e) {
          // Ignora datas inválidas
        }
        return false
      }).length

      days.push({
        date,
        dateString,
        bookingsCount,
        isToday: dateString === today,
        isCurrentMonth: date.getMonth() === month,
      })
    }

    return days
  }, [currentMonth, allBookings])

  // Confirma reserva com verificação de mesa
  const checkTableAllocation = async (bookingId: number, booking: BookingType) => {
    try {
      setLoading(true)

      // Guardar booking para usar na alocação
      console.log('💾 Guardando booking para alocação:', booking)
      setBookingForAllocation(booking)

      const res = await POST(`/queueReservation/booking/${bookingId}/suggest-table-allocation`, {})
      setLoading(false)

      if (res && res.success) {
        setTableSuggestions(res.data)
        setShowTableSuggestionModal(true)
      } else {
        await confirmBookingWithoutTable('confirmed')
      }
    } catch (e: any) {
      setLoading(false)
      console.log('Erro ao buscar sugestões:', e)
      await confirmBookingWithoutTable('confirmed')
    }
  }

  // Verificar se uma booking tem alocações
  const checkBookingHasAllocations = async (bookingId: number) => {
    try {
      const res = await GET(`/table-management/bookings/${bookingId}/allocations`)
      if (res && res.data && Array.isArray(res.data) && res.data.length > 0) {
        setBookingsWithAllocations(prev => new Set([...prev, bookingId]))
        return true
      }
      return false
    } catch (e) {
      console.log('Erro ao verificar alocações:', e)
      return false
    }
  }

  // Propor alocação com validação de mesas já alocadas
  const proposeTableAllocationWithCheck = async (booking: BookingType) => {
    try {
      // Verificar se já tem alocações
      if (bookingsWithAllocations.has(booking.id)) {
        Swal.fire({
          icon: 'info',
          title: 'Mesa já alocada',
          text: 'Esta reserva já possui mesas alocadas',
        })
        return
      }

      // Fazer check no backend
      const hasAllocations = await checkBookingHasAllocations(booking.id)
      if (hasAllocations) {
        Swal.fire({
          icon: 'info',
          title: 'Mesa já alocada',
          text: 'Esta reserva já possui mesas alocadas',
        })
        return
      }

      // Se passou nas validações, propor alocação
      await proposeTableAllocation(booking, true)
    } catch (e) {
      console.log('Erro ao validar alocações:', e)
    }
  }

  // Verificar se uma booking tem alocações

  // Propor alocação de mesa apenas (sem confirmar)
  const proposeTableAllocation = async (booking: BookingType, openModal: boolean = false) => {
    try {
      setLoading(true)
      setBookingForAllocation(booking)
      
      // Se houver mesas alocadas, mostrar aviso
      if (allocatedTables.length > 0) {
        setLoading(false)
        Swal.fire({
          icon: 'info',
          title: 'Mesas Já Alocadas',
          text: `Esta reserva já possui ${allocatedTables.length} mesa(s) alocada(s). Para propor novas alocações, libere as mesas primeiro.`,
        })
        return
      }
      
      const res = await POST(`/queueReservation/booking/${booking.id}/suggest-table-allocation`, {})
      setLoading(false)
      if (res && res.success) {
        setTableSuggestions(res.data)
        if (openModal) {
          // Abre modal quando clicado do card
          setShowTableSuggestionModal(true)
        } else {
          // Expande accordion quando clicado dentro do modal de detalhes
          setExpandSuggestionsAccordion(true)
        }
      } else {
        Swal.fire({icon: 'warning', title: 'Sem mesas disponíveis', text: 'Não há mesas vagas para este período'})
      }
    } catch (e: any) {
      setLoading(false)
      Swal.fire({icon: 'error', title: 'Erro', text: 'Falha ao buscar sugestões'})
    }
  }

  // Aloca mesa para reserva
  const allocateTable = async (suggestion: TableCombination) => {
    console.log('🎯 allocateTable chamado', {suggestion, bookingForAllocation})

    if (!bookingForAllocation) {
      console.log('❌ bookingForAllocation está vazio')
      return
    }

    try {
      setLoading(true)

      const payload = {
        bookingId: bookingForAllocation.id,
        environmentTableIds: suggestion.tableIds,
        userId: Number(localStorage.getItem('userId')) || 1,
      }

      console.log('📤 Enviando payload:', payload)

      const res = await POST(`/queueReservation/tableAllocation/allocate`, payload)

      console.log('📥 Resposta recebida:', res)

      setLoading(false)

      if (res && res.success) {
        Swal.fire({
          icon: 'success',
          title: 'Mesa alocada!',
          text: `Mesa(s) ${suggestion.tableNumbers.join(', ')} alocada(s) com sucesso.`,
        }).then(async () => {
          // Atualizar allocatedTables imediatamente com os dados alocados
          if (res.data && Array.isArray(res.data)) {
            console.log('✅ Atualizando allocatedTables com resposta:', res.data)
            setAllocatedTables(res.data)
          }
          // Fecha o accordion de sugestões
          setExpandSuggestionsAccordion(false)
          setTableSuggestions(null)
          
          // Garante que bookingSelected está setado
          if (!bookingSelected && bookingForAllocation) {
            console.log('⚠️ bookingSelected estava vazio, setando com bookingForAllocation')
            setBookingSelected(bookingForAllocation)
          }
          
          // Confirma a reserva (isso vai fazer PUT e mostrar novo alert) 
          console.log('📋 Confirmando reserva após alocação de mesa', {bookingSelected, bookingForAllocation})
          await confirmBookingWithoutTable('confirmed')
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro!',
          text: res?.message || 'Falha ao alocar mesa',
        })
      }
    } catch (e: any) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: e?.response?.data?.message || 'Falha ao alocar mesa',
      })
    }
  }

  // Confirma reserva sem alocação de mesa
  const confirmBookingWithoutTable = async (action?: string) => {
    // Usar bookingSelected se estiver disponível (dentro do modal de detalhes)
    // Senão usar bookingForAllocation (caso vindo de fora do modal)
    const bookingToConfirm = bookingSelected || bookingForAllocation

    console.log('🔍 DEBUG confirmBookingWithoutTable:', {
      bookingSelected: bookingSelected?.id,
      bookingForAllocation: bookingForAllocation?.id,
      bookingIdQueSeraConfirmado: bookingToConfirm?.id,
      action,
    })

    if (!bookingToConfirm) {
      console.log('❌ Nenhum booking disponível para confirmar')
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: 'Nenhuma reserva encontrada para confirmar',
      })
      return
    }

    const booking = Object.assign(bookingToConfirm)
    const opt = action || option.toString()

    console.log('✅ Confirmando reserva:', {bookingId: booking.id, action: opt})

    try {
      setLoading(true)
      const res = await PUT(`/queueReservation/booking/${booking.id}`, {
        finalized: opt,
        bookingConfigId: booking.bookingConfigId,
      })
      setLoading(false)

      console.log('📡 Resposta da API:', {success: res?.success})

      if (res && res.success) {
        Swal.fire({
          icon: 'success',
          title: 'Reserva alterada!',
        }).then(async () => {
          console.log('🚪 Modal vai fechar - limpando states')
          // Limpa os estados PRIMEIRO
          setBookingSelected(null)
          setBookingForAllocation(null)
          setTableSuggestions(null)
          setSelectedSuggestionIndex(null)
          setExpandSuggestionsAccordion(false)
          setOption('')
          
          // Agora fecha o modal
          console.log('🚪 Chamando closeWrapperModal()')
          closeWrapperModal()
          
          // Recarrega os dados
          console.log('🔄 Recarregando dados com init()')
          await init()
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro!',
          text: 'Falha ao alterar reserva',
        })
      }
    } catch (e: any) {
      setLoading(false)
      console.log('❌ Erro ao confirmar:', e)
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: e ? e : 'Falha ao alterar reserva',
      })
    } finally {
      // Limpa apenas o option
      // Outros estados são limpos no .then() do alert
      setOption('')
    }
  }

  // Confirmar sem mesa a partir do modal de sugestões
  const confirmWithoutTableFromSuggestions = async () => {
    console.log('🎯 confirmWithoutTableFromSuggestions chamado - BOTÃO CLICADO')
    console.log('Current State:', {
      bookingSelected: bookingSelected?.id,
      bookingForAllocation: bookingForAllocation?.id,
      tableSuggestions: !!tableSuggestions,
    })
    await confirmBookingWithoutTable('confirmed')
    // Não precisa limpar aqui novamente, pois confirmBookingWithoutTable já faz no finally
    setExpandSuggestionsAccordion(false)
  }

  // Muda status da reserva
  const changeBooking = async () => {
    if (bookingSelected) {
      // Determina a ação baseada no estado atual da reserva
      const action = option === 'canceled' ? 'canceled' : 'confirmed'

      if (action === 'confirmed' && enableTableManagement && tableSuggestions && tableSuggestions.suggestions.length > 0) {
        // Se tem sugestão selecionada via radioButton, usa aquela
        let suggestionToUse
        if (selectedSuggestionIndex !== null) {
          console.log('🎯 Usando sugestão selecionada:', selectedSuggestionIndex)
          suggestionToUse = tableSuggestions.suggestions[selectedSuggestionIndex]
        } else {
          // Se não tem nenhuma selecionada, usa a PRIMEIRA
          console.log('🎯 Nenhuma sugestão selecionada, usando primeira')
          suggestionToUse = tableSuggestions.suggestions[0]
        }
        await allocateTable(suggestionToUse)
      } else {
        // Se não tem sugestões ou é cancelamento, apenas confirma
        setOption(action)
        await confirmBookingWithoutTable(action)
      }
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: 'Falha ao alterar reserva',
      })
    }
  }

  // Inicialização
  const init = async () => {
    setLoading(true)
    const config = await getBookingConfig()
    if (config && config.id) {
      const envList = config.environment && config.environment.length > 0 ? config.environment : []
      await getAllBookings(config.id, envList)
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: 'Não foi possível encontrar configuração de reserva',
      }).then(() => navigate('/booking/config'))
    }
    setLoading(false)
  }

  useEffect(() => {
    // Restaura filtros do localStorage ANTES de qualquer carregamento de dados
    const storedFilters = loadFiltersFromStorage()
    if (storedFilters) {
      if (storedFilters.selectedEnvironment !== undefined) {
        setSelectedEnvironment(storedFilters.selectedEnvironment)
      }
      
      if (storedFilters.selectedDate !== undefined && storedFilters.selectedDate !== '') {
        setSelectedDate(storedFilters.selectedDate)
      }
      
      if (storedFilters.selectedHour !== undefined) {
        setSelectedHour(storedFilters.selectedHour)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Carrega dados iniciais DEPOIS que os filtros foram restaurados
    const loadData = async () => {
      await init()
      // Marca que restauração terminou para que getAllBookings possa sobrescrever se necessário
      setIsRestoringFromStorage(false)
    }
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Salva filtros no localStorage quando mudarem (mas não se data estiver vazia)
  useEffect(() => {
    // Guard: não salvar se data estiver vazia
    if (selectedDate === '' || selectedDate === undefined) {
      return
    }
    
    const filters: StoredFilters = {
      selectedEnvironment,
      selectedDate,
      selectedHour,
    }
    saveFiltersToStorage(filters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvironment, selectedDate, selectedHour])

  // Atualiza bookings filtrados
  useEffect(() => {
    setBookings(filteredBookings)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBookings])

  // Valida data quando availableDates muda (ex: mudança de ambiente)
  useEffect(() => {
    if (availableDates.length > 0) {
      // Se data selecionada não está na lista de datas disponíveis, seleciona a data mais próxima
      if (!availableDates.includes(selectedDate)) {
        // Tenta usar a data de hoje se estiver disponível
        const today = new Date().toISOString().split('T')[0]
        if (availableDates.includes(today)) {
          setSelectedDate(today)
        } else {
          // Caso contrário, seleciona a primeira data disponível
          setSelectedDate(availableDates[0])
        }
      }
    }
  }, [availableDates])

  // Reseta horário se ele não estiver mais disponível após filtrar por ambiente/data
  useEffect(() => {
    if (selectedHour !== 'all' && dynamicAvailableHours.length > 0) {
      if (!dynamicAvailableHours.includes(selectedHour)) {
        console.log(`⚠️ Horário ${selectedHour} não disponível após filtros. Resetando para 'all'`)
        setSelectedHour('all')
      }
    }
  }, [dynamicAvailableHours])

  // Auto-hide suggestions accordion when allocated tables are populated
  useEffect(() => {
    if (allocatedTables.length > 0) {
      console.log('✅ Tabelas alocadas detectadas. Limpando sugestões.')
      setExpandSuggestionsAccordion(false)
      setTableSuggestions(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocatedTables])

  // Contador vivo que atualiza a cada segundo
  useEffect(() => {
    if (bookingSelected && bookingSelected.secondsToExpires > 0) {
      setLiveTimer(bookingSelected.secondsToExpires)

      const interval = setInterval(() => {
        setLiveTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval)
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(interval)
    } else if (bookingSelected) {
      setLiveTimer(bookingSelected.secondsToExpires)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingSelected])

  // Navega para o mês anterior
  const previousMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
  }

  // Navega para o próximo mês
  const nextMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
  }

  // Seleciona data do calendário
  const selectCalendarDate = (dateString: string) => {
    setSelectedDate(dateString)
    setViewMode('day')
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5'>
          {/* Card de Estatísticas */}
          <div className='col-12'>
            <div className='row g-4'>
              <div className='col-md-3'>
                <div className='card shadow-sm h-100'>
                  <div className='card-body text-center'>
                    <i className='bi bi-calendar-check text-primary fs-2x mb-3'></i>
                    <h3 className='fw-bold text-gray-800'>{statistics.total}</h3>
                    <p className='text-muted mb-0'>Total de Reservas</p>
                  </div>
                </div>
              </div>

              <div className='col-md-3'>
                <div className='card shadow-sm h-100'>
                  <div className='card-body text-center'>
                    <i className='bi bi-check-circle text-success fs-2x mb-3'></i>
                    <h3 className='fw-bold text-success'>{statistics.confirmed}</h3>
                    <p className='text-muted mb-0'>Confirmadas</p>
                    <small className='text-muted'>
                      {statistics.total > 0
                        ? Math.round((statistics.confirmed / statistics.total) * 100)
                        : 0}
                      %
                    </small>
                  </div>
                </div>
              </div>

              <div className='col-md-3'>
                <div className='card shadow-sm h-100'>
                  <div className='card-body text-center'>
                    <i className='bi bi-people text-info fs-2x mb-3'></i>
                    <h3 className='fw-bold text-info'>{statistics.totalPeople}</h3>
                    <p className='text-muted mb-0'>Total de Pessoas</p>
                    <small className='text-muted'>Capacidade: {statistics.totalCapacity}</small>
                  </div>
                </div>
              </div>

              <div className='col-md-3'>
                <div className='card shadow-sm h-100'>
                  <div className='card-body text-center'>
                    <i className='bi bi-graph-up text-warning fs-2x mb-3'></i>
                    <h3 className='fw-bold text-warning'>{statistics.occupancyRate.toFixed(1)}%</h3>
                    <p className='text-muted mb-0'>Taxa de Ocupação</p>
                    <small className='text-muted'>Vagas: {statistics.availableSeats}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card de Filtros e Visualização */}
          <div className='col-12'>
            <div className='card shadow-sm'>
              <div className='card-header bg-light d-flex justify-content-between align-items-center'>
                <h3 className='card-title fw-bold mb-0'>
                  <i className='bi bi-funnel text-primary me-2'></i>
                  Filtros e Visualização
                </h3>

                {/* Botões de Visualização */}
                <div className='btn-group' role='group'>
                  <button
                    type='button'
                    className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setViewMode('list')}
                    title='Visualização em Lista'
                  >
                    <i className='bi bi-list-ul'></i>
                  </button>
                  <button
                    type='button'
                    className={`btn btn-sm ${viewMode === 'month' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setViewMode('month')}
                    title='Visualização Mensal'
                  >
                    <i className='bi bi-calendar3'></i>
                  </button>
                  <button
                    type='button'
                    className={`btn btn-sm ${viewMode === 'day' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setViewMode('day')}
                    title='Visualização Diária'
                  >
                    <i className='bi bi-calendar-day'></i>
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    onClick={() => init()}
                    title='Recarregar com último filtro aplicado'
                  >
                    <i className='bi bi-arrow-clockwise'></i>
                  </button>
                </div>
              </div>

              <div className='card-body'>
                <div className='row g-3'>
                  {/* Filtro de Busca por Nome/Telefone */}
                  <div className='col-md-12'>
                    <label className='form-label fw-bold fs-6'>
                      <i className='bi bi-search me-1'></i>
                      Buscar por Nome ou Telefone
                    </label>
                    <div className='input-group'>
                      <span className='input-group-text'>
                        <i className='bi bi-search'></i>
                      </span>
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Digite o nome ou telefone do cliente...'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <button
                          className='btn btn-light'
                          type='button'
                          onClick={() => setSearchTerm('')}
                          title='Limpar busca'
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                    {searchTerm && (
                      <small className='text-muted mt-1 d-block'>
                        {filteredBookings.length}{' '}
                        {filteredBookings.length === 1
                          ? 'resultado encontrado'
                          : 'resultados encontrados'}
                      </small>
                    )}
                  </div>

                  {/* Filtro de Ambiente */}
                  <div className='col-md-3'>
                    <label className='form-label fw-bold fs-6'>
                      <i className='bi bi-building me-1'></i>
                      Ambiente
                    </label>
                    <select
                      className='form-select'
                      value={selectedEnvironment}
                      onChange={(e) =>
                        setSelectedEnvironment(
                          e.target.value === 'all' ? 'all' : Number(e.target.value)
                        )
                      }
                    >
                      <option value='all'>Todos os Ambientes</option>
                      {environments.map((env) => (
                        <option key={env.id} value={env.id}>
                          {env.name} (Cap: {env.seatsAvailable})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Filtro de Data */}
                  <div className='col-md-3'>
                    <label className='form-label fw-bold fs-6'>
                      <i className='bi bi-calendar-date me-1'></i>
                      Data
                      {availableDates.length > 0 && (
                        <span className='badge badge-light-primary ms-2 fs-8'>
                          {availableDates.length} {availableDates.length === 1 ? 'dia' : 'dias'} com
                          reservas
                        </span>
                      )}
                    </label>
                    <input
                      type='date'
                      className='form-control'
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      min={availableDates.length > 0 ? availableDates[0] : undefined}
                      max={
                        availableDates.length > 0
                          ? availableDates[availableDates.length - 1]
                          : undefined
                      }
                    />
                    {selectedDate && (
                      <small className='text-muted mt-1 d-block'>
                        {(() => {
                          const bookingsOnDate = allBookings.filter((bk) => {
                            try {
                              const bkDate = new Date(bk.date)
                              if (!isNaN(bkDate.getTime())) {
                                return bkDate.toISOString().split('T')[0] === selectedDate
                              }
                            } catch (e) {
                              // Ignora
                            }
                            return false
                          }).length
                          return bookingsOnDate > 0
                            ? `${bookingsOnDate} ${
                                bookingsOnDate === 1 ? 'reserva' : 'reservas'
                              } neste dia`
                            : 'Nenhuma reserva neste dia'
                        })()}
                      </small>
                    )}
                  </div>

                  {/* Filtro de Horário + Botão Recarregar */}
                  <div className='col-md-3'>
                    <label className='form-label fw-bold fs-6'>
                      <i className='bi bi-clock me-1'></i>
                      Horário
                    </label>
                    <select
                      className='form-select'
                      value={selectedHour}
                      onChange={(e) => setSelectedHour(e.target.value)}
                    >
                      <option value='all'>Todos os Horários</option>
                      {dynamicAvailableHours.map((hour) => (
                        <option key={hour} value={hour}>
                          {hour}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Visualização de Calendário Mensal */}
          {viewMode === 'month' && (
            <div className='col-12'>
              <div className='card shadow-sm'>
                <div className='card-header bg-light d-flex justify-content-between align-items-center'>
                  <button className='btn btn-sm btn-light' onClick={previousMonth}>
                    <i className='bi bi-chevron-left'></i>
                  </button>
                  <h4 className='fw-bold mb-0'>
                    {currentMonth.toLocaleDateString('pt-BR', {month: 'long', year: 'numeric'})}
                  </h4>
                  <button className='btn btn-sm btn-light' onClick={nextMonth}>
                    <i className='bi bi-chevron-right'></i>
                  </button>
                </div>
                <div className='card-body p-0'>
                  <div className='table-responsive'>
                    <table className='table table-bordered mb-0'>
                      <thead className='bg-light'>
                        <tr>
                          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day) => (
                            <th key={day} className='text-center p-3 fw-bold'>
                              {day}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({length: 6}, (_, weekIndex) => (
                          <tr key={weekIndex}>
                            {calendarDays
                              .slice(weekIndex * 7, (weekIndex + 1) * 7)
                              .map((day, dayIndex) => (
                                <td
                                  key={dayIndex}
                                  className={`p-3 cursor-pointer position-relative ${
                                    !day.isCurrentMonth ? 'bg-light text-muted' : ''
                                  } ${day.isToday ? 'bg-light-primary' : ''}`}
                                  style={{
                                    height: '100px',
                                    verticalAlign: 'top',
                                    cursor: day.bookingsCount > 0 ? 'pointer' : 'default',
                                  }}
                                  onClick={() =>
                                    day.bookingsCount > 0 && selectCalendarDate(day.dateString)
                                  }
                                >
                                  <div className='d-flex justify-content-between'>
                                    <span
                                      className={`fw-bold ${day.isToday ? 'text-primary' : ''}`}
                                    >
                                      {day.date.getDate()}
                                    </span>
                                    {day.bookingsCount > 0 && (
                                      <span className='badge badge-primary badge-sm'>
                                        {day.bookingsCount}
                                      </span>
                                    )}
                                  </div>
                                  {day.bookingsCount > 0 && (
                                    <div className='mt-2'>
                                      <small className='text-muted'>
                                        {day.bookingsCount}{' '}
                                        {day.bookingsCount === 1 ? 'reserva' : 'reservas'}
                                      </small>
                                    </div>
                                  )}
                                </td>
                              ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Visualização Diária / Lista */}
          {(viewMode === 'day' || viewMode === 'list') && (
            <div className='col-12'>
              <div className='card shadow-sm'>
                <div className='card-header bg-light'>
                  <h3 className='card-title fw-bold mb-0'>
                    <i className='bi bi-calendar-check text-primary me-2'></i>
                    Reservas
                    {selectedDate && (
                      <span className='text-muted fs-6 ms-2'>- {formatDateBR(selectedDate)}</span>
                    )}
                  </h3>
                </div>

                <div className='card-body'>
                  {bookings.length > 0 ? (
                    <div className='d-flex flex-column gap-4'>
                      {bookings.map((booking) => (
                        <div
                          key={booking.id}
                          className='border rounded p-4'
                          style={{
                            borderLeft:
                              booking.finalized === 'confirmed' || booking.customerArrived === 1
                                ? '4px solid #50cd89 !important'
                                : booking.finalized === 'canceled'
                                ? '4px solid #f1416c !important'
                                : '4px solid #ffc700 !important',
                          }}
                        >
                          <div className='row align-items-center'>
                            <div className='col-md-8'>
                              <div className='d-flex flex-column gap-2'>
                                <div className='d-flex align-items-center gap-3'>
                                  <h5 className='fw-bold mb-0'>{booking.name}</h5>
                                  {booking.finalized === 'confirmed' ||
                                  booking.customerArrived === 1 ? (
                                    <span className='badge badge-success'>Confirmada</span>
                                  ) : booking.finalized === 'canceled' ? (
                                    <span className='badge badge-danger'>Cancelada</span>
                                  ) : booking.finalized === 'noShow' &&
                                    !isBookingInFuture(booking) ? (
                                    <span className='badge badge-warning'>Não Compareceu</span>
                                  ) : (
                                    <span className='badge badge-info'>Aguardando</span>
                                  )}
                                </div>

                                <div className='d-flex flex-wrap gap-4'>
                                  <span className='text-muted'>
                                    <i className='bi bi-clock me-1'></i>
                                    {booking.bookingHour?.hour}
                                  </span>
                                  <span className='text-muted'>
                                    <i className='bi bi-people me-1'></i>
                                    {booking.people} pessoas
                                  </span>
                                  <span className='text-muted'>
                                    <i className='bi bi-building me-1'></i>
                                    {booking.environment}
                                  </span>
                                  <span className='text-muted'>
                                    <i className='bi bi-telephone me-1'></i>
                                    {booking.phone}
                                  </span>
                                  {booking.secondsToExpires > 0 &&
                                    (!booking.finalized || booking.finalized === 'pending' || booking.finalized === 'Pendente') && (
                                      <span
                                        className={
                                          booking.alarm ? 'text-warning fw-bold' : 'text-muted'
                                        }
                                      >
                                        <i className='bi bi-hourglass-split me-1'></i>
                                        Expira em: {secondsToHour(booking.secondsToExpires)}
                                      </span>
                                    )}
                                </div>
                              </div>
                            </div>

                            <div className='col-md-4'>
                              <div className='d-flex justify-content-end gap-2'>
                                {(!booking.finalized || booking.finalized === 'pending' || booking.finalized === 'Pendente') && (
                                  <>
                                    <button
                                      className='btn btn-sm btn-success'
                                      onClick={() => {
                                        if (enableTableManagement) {
                                          checkTableAllocation(booking.id, booking)
                                        } else {
                                          setBookingSelected(booking)
                                          setOption('confirmed')
                                          showWrapperModal('Confirmar Reserva')
                                        }
                                      }}
                                      title={
                                        enableTableManagement
                                          ? 'Confirmar e Alocar Mesa'
                                          : 'Confirmar'
                                      }
                                    >
                                      <i className='bi bi-check-lg'></i>
                                    </button>
                                    <button
                                      className='btn btn-sm btn-danger'
                                      onClick={() => {
                                        setBookingSelected(booking)
                                        setOption('canceled')
                                        showWrapperModal('Cancelar Reserva')
                                      }}
                                      title='Cancelar'
                                    >
                                      <i className='bi bi-x-lg'></i>
                                    </button>
                                  </>
                                )}
                                <button
                                  className='btn btn-sm btn-light'
                                  onClick={() => {
                                    setBookingSelected(booking)
                                    setOption('view')
                                    fetchAllocatedTables(booking.id)
                                    showWrapperModal('Detalhes da Reserva')
                                  }}
                                  title='Ver Detalhes'
                                >
                                  <i className='bi bi-eye'></i>
                                </button>
                                {enableTableManagement && !(booking.finalized === 'noShow' && !isBookingInFuture(booking)) && (
                                  <button className='btn btn-sm btn-info' onClick={() => proposeTableAllocationWithCheck(booking)} title='Propor Alocação'>
                                    <Armchair size={16} className='inline' />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className='text-center py-10'>
                      <i className='bi bi-inbox fs-3x text-muted mb-4'></i>
                      <h4 className='text-muted'>Nenhuma reserva encontrada</h4>
                      <p className='text-muted'>
                        Tente ajustar os filtros para visualizar outras reservas
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Modal de Confirmação */}
          <ModalWrapper
            confirmAction={() => changeBooking()}
            textAction={option === 'canceled' ? 'Cancelar Reserva' : 'Confirmar Reserva'}
            noAction={
              !bookingSelected ||
              bookingSelected.finalized === 'confirmed' ||
              bookingSelected.finalized === 'canceled' ||
              bookingSelected.customerArrived === 1
            }
            disableMainButton={
              bookingSelected
                ? bookingSelected.finalized === 'canceled' ||
                  (bookingSelected.finalized === 'noShow' && !isBookingInFuture(bookingSelected))
                : false
            }
          >
            {bookingSelected && (
              <div className='border rounded p-4'>
                {(bookingSelected.finalized === 'canceled' ||
                  bookingSelected.finalized === 'noShow') && (
                  <div className='alert alert-warning mb-3'>
                    <i className='bi bi-exclamation-triangle me-2'></i>
                    Esta reserva está{' '}
                    {bookingSelected.finalized === 'canceled'
                      ? 'cancelada'
                      : 'marcada como "Não Compareceu"'}{' '}
                    e não pode ser confirmada.
                  </div>
                )}
                <div className='d-flex flex-column gap-2'>
                  <p className='mb-1'>
                    <strong>Nome:</strong> {bookingSelected.name}
                  </p>
                  <p className='mb-1'>
                    <strong>Telefone:</strong> {bookingSelected.phone}
                  </p>
                  <p className='mb-1'>
                    <strong>Email:</strong> {bookingSelected.email}
                  </p>
                  <p className='mb-1'>
                    <strong>Data:</strong> {formatDateBR(bookingSelected.date)}
                  </p>
                  <p className='mb-1'>
                    <strong>Horário:</strong> {bookingSelected.bookingHour?.hour}
                  </p>
                  <p className='mb-1'>
                    <strong>Pessoas:</strong> {bookingSelected.people}
                  </p>
                  <p className='mb-1'>
                    <strong>Ambiente:</strong> {bookingSelected.environment || 'N/A'}
                    {(() => {
                      const env = environments.find(
                        (e) => e.id === bookingSelected.bookingEnvironmentId
                      )
                      if (env) {
                        const price = calculateEnvironmentPrice(env, bookingSelected.people)
                        if (price > 0) {
                          return (
                            <span className='text-success fw-bold'>
                              {' '}
                              - R$ {price.toFixed(2).replace('.', ',')}
                            </span>
                          )
                        }
                      }
                      return null
                    })()}
                  </p>
                  {bookingSelected.payment && bookingSelected.payment > 0 && (
                    <p className='mb-1'>
                      <strong>Valor Pago:</strong> R${' '}
                      {bookingSelected.payment.toFixed(2).replace('.', ',')}
                    </p>
                  )}
                  {(() => {
                    const shouldShowTimer =
                      bookingSelected.finalized !== 'confirmed' &&
                      bookingSelected.finalized !== 'canceled' &&
                      bookingSelected.customerArrived !== 1

                    if (shouldShowTimer && liveTimer > 0) {
                      const isAlarm = liveTimer / 60 < expiryTimeLimit
                      return (
                        <p className='mb-1'>
                          <strong>Tempo até expirar:</strong>{' '}
                          <span className={isAlarm ? 'text-danger fw-bold' : 'text-muted'}>
                            {secondsToHour(liveTimer)}
                            {isAlarm && ' ⚠️'}
                          </span>
                        </p>
                      )
                    }
                    return null
                  })()}
                  {/* Mesas Alocadas */}
                  {enableTableManagement && allocatedTables.length > 0 && (
                    <p className='mb-1'>
                      <strong>Mesas Alocadas:</strong>
                      <div className='ms-2 mt-2 d-flex gap-2 flex-wrap'>
                        {allocatedTables.map((table: any) => (
                          <span
                            key={table.id}
                            className={`badge ${
                              table.status === 'occupied'
                                ? 'bg-danger'
                                : table.status === 'cleaning'
                                ? 'bg-warning text-dark'
                                : 'bg-info'
                            }`}
                          >
                            <i className='bi bi-table me-1'></i>
                            Mesa {table.environmentTable?.tableNumber || table.tableNumber || 'N/A'}
                            <small className='ms-1'>
                              ({table.status === 'allocated'
                                ? '✓ Alocada'
                                : table.status === 'occupied'
                                ? '⭐ Ocupada'
                                : '🧹 Limpando'})
                            </small>
                          </span>
                        ))}
                      </div>
                    </p>
                  )}
                  {enableTableManagement && allocatedTables.length === 0 && (
                    <div className='mb-3'>
                      <p className='mb-2'>
                        <strong>Mesas Alocadas:</strong>
                        <span className='text-muted ms-2' style={{fontStyle: 'italic'}}>
                          Nenhuma mesa alocada
                        </span>
                      </p>
                      <button className='btn btn-sm btn-outline-info' onClick={() => bookingSelected && proposeTableAllocation(bookingSelected)} title='Propor uma alocação de mesa para esta reserva'>
                        <Armchair size={14} className='me-1 inline-block' />
                        Propor Alocação de Mesa
                      </button>
                    </div>
                  )}

                  {enableTableManagement && tableSuggestions && expandSuggestionsAccordion && allocatedTables.length === 0 && (
                    <div className='accordion mt-3 border' id='acc1'>
                      <div className='accordion-item border-0'>
                        <h2 className='accordion-header'>
                          <button className='accordion-button fw-bold text-dark' type='button' data-bs-toggle='collapse' data-bs-target='#col1' aria-expanded='true'>
                            <i className='bi bi-lightbulb me-2 text-warning'></i>Sugestões de Alocação ({tableSuggestions?.suggestions?.length || 0})
                          </button>
                        </h2>
                        <div id='col1' className='accordion-collapse collapse show'>
                          <div className='accordion-body p-3'>
                            {console.log('✅ ACCORDION RENDERIZADO - Condições:', {
                              enableTableManagement,
                              temTableSuggestions: !!tableSuggestions,
                              expandSuggestionsAccordion,
                              allocatedTablesLength: allocatedTables.length,
                            })}
                            <div className='mb-3 p-2 bg-light-warning rounded'>
                              <p className='mb-1 small'><strong>Reserva:</strong> {tableSuggestions?.booking?.name}</p>
                              <p className='mb-1 small'><strong>Pessoas:</strong> {tableSuggestions?.booking?.people} | <strong>Horário:</strong> {tableSuggestions?.booking?.hour}</p>
                              <p className='mb-0 small'><strong>Ambiente:</strong> {tableSuggestions?.environment?.name}</p>
                            </div>
                            {tableSuggestions?.hasAvailableTables ? (
                              <div style={{maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px'}}>
                                {tableSuggestions?.suggestions?.map((s: any, idx: number) => (
                                  <div key={idx} className='card mb-2'>
                                    <div className='card-body p-2'>
                                      <div className='d-flex justify-content-between align-items-start gap-2'>
                                        <div className='d-flex gap-2 align-items-start flex-grow-1'>
                                          <input
                                            type='radio'
                                            id={`suggestion-${idx}`}
                                            name='table-suggestions'
                                            value={idx}
                                            checked={selectedSuggestionIndex === idx}
                                            onChange={() => setSelectedSuggestionIndex(idx)}
                                            style={{marginTop: '4px', cursor: 'pointer'}}
                                          />
                                          <label htmlFor={`suggestion-${idx}`} style={{cursor: 'pointer', flex: 1}}>
                                            <h6 className='mb-2'>Opção {idx + 1}</h6>
                                            <p className='mb-1 small'><strong>Mesas:</strong> {s.tableNumbers?.join(', ')}</p>
                                            <p className='mb-1 small'><strong>Cap:</strong> {s.totalCapacity} lugares</p>
                                            <p className='mb-0 small'><strong>Desperdício:</strong> <span style={{color: s.wastePercentage <= 20 ? '#28a745' : s.wastePercentage <= 40 ? '#ffc107' : '#dc3545'}}>{s.wastePercentage}%</span></p>
                                          </label>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className='alert alert-warning mb-0'>Sem mesas disponíveis.</div>
                            )}
                            <div className='d-flex gap-2 mt-3'>
                              <button className='btn btn-sm btn-outline-secondary flex-grow-1' onClick={() => {setExpandSuggestionsAccordion(false); setTableSuggestions(null); setSelectedSuggestionIndex(null); closeWrapperModal(); setBookingSelected(null);}}>Recusar</button>
                              <button className='btn btn-sm btn-success flex-grow-1' onClick={() => confirmWithoutTableFromSuggestions()}>Confirmar sem Mesa</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Botões de Ação para Mesas */}
                  {enableTableManagement && allocatedTables.length > 0 && (
                    <div className='mt-3 mb-3 p-2 bg-light rounded'>
                      <p className='fw-bold text-dark mb-2'>
                        <i className='bi bi-arrow-repeat me-2'></i>
                        Alterar Estado das Mesas
                      </p>
                      <div className='d-flex gap-2 flex-wrap'>
                        {allocatedTables.map((table: any) => (
                          <div key={table.id} className='d-flex gap-1'>
                            <button
                              className='btn btn-sm btn-outline-danger'
                              onClick={() => updateTableStatus(table.id, 'occupied')}
                              disabled={table.status === 'occupied'}
                              title='Marcar mesa como ocupada'
                            >
                              <i className='bi bi-check-circle me-1'></i>
                              Mesa {table.environmentTable?.tableNumber || table.tableNumber || 'N/A'} - Ocupada
                            </button>
                            <button
                              className='btn btn-sm btn-outline-warning'
                              onClick={() => updateTableStatus(table.id, 'cleaning')}
                              disabled={table.status === 'cleaning'}
                              title='Marcar mesa como limpando'
                            >
                              <i className='bi bi-brush me-1'></i>
                              Limpando
                            </button>
                          </div>
                        ))}
                      </div>
                      <small className='text-muted d-block mt-2'>
                        💡 Cliente chegou? Clique em "Ocupada". Cliente saiu? Clique em "Limpando".
                      </small>
                    </div>
                  )}

                  <p className='mb-0'>
                    <strong>Status:</strong>{' '}
                    <span
                      className={`badge ${
                        bookingSelected.finalized === 'confirmed' ||
                        bookingSelected.customerArrived === 1
                          ? 'bg-success'
                          : bookingSelected.finalized === 'canceled'
                          ? 'bg-danger'
                          : bookingSelected.finalized === 'noShow' &&
                            !isBookingInFuture(bookingSelected)
                          ? 'bg-warning text-dark'
                          : 'bg-info'
                      }`}
                    >
                      {bookingSelected.finalized === 'confirmed' ||
                      bookingSelected.customerArrived === 1
                        ? 'Confirmada'
                        : bookingSelected.finalized === 'canceled'
                        ? 'Cancelada'
                        : bookingSelected.finalized === 'noShow' &&
                          !isBookingInFuture(bookingSelected)
                        ? 'Não Compareceu'
                        : 'Aguardando Confirmação'}
                    </span>
                  </p>

                  {/* Accordion - Saiba Mais (Uso Geral) */}
                  <div className='accordion mt-4 border-top pt-3' id='accordionBookingInfo'>
                    <div className='accordion-item border-0'>
                      <h2 className='accordion-header' id='headingBookingInfo'>
                        <button
                          className='accordion-button collapsed fw-bold text-dark'
                          type='button'
                          data-bs-toggle='collapse'
                          data-bs-target='#collapseBookingInfo'
                          aria-expanded='false'
                          aria-controls='collapseBookingInfo'
                        >
                          <i className='bi bi-info-circle me-2 text-info'></i>
                          Saiba mais...
                        </button>
                      </h2>
                      <div
                        id='collapseBookingInfo'
                        className='accordion-collapse collapse'
                        aria-labelledby='headingBookingInfo'
                        data-bs-parent='#accordionBookingInfo'
                      >
                        <div className='accordion-body bg-light-info p-3'>
                          {/* Status da Reserva */}
                          <div className='mb-4'>
                            <h6 className='fw-bold text-dark mb-3'>
                              <i className='bi bi-bookmark-check me-2'></i>
                              Entender o Status da Reserva
                            </h6>
                            <div className='row g-3'>
                              <div className='col-12'>
                                <div className='d-flex align-items-center gap-2 mb-2'>
                                  <span className='badge bg-info'>ℹ️</span>
                                  <strong className='text-dark'>Aguardando Confirmação</strong>
                                </div>
                                <p className='text-muted small mb-0'>
                                  Reserva foi criada mas o cliente ainda não confirmou presença. Pode ser cancelada
                                  pelo cliente ou expirar automaticamente.
                                </p>
                              </div>
                              <div className='col-12'>
                                <div className='d-flex align-items-center gap-2 mb-2'>
                                  <span className='badge bg-success'>✓</span>
                                  <strong className='text-dark'>Confirmada</strong>
                                </div>
                                <p className='text-muted small mb-0'>
                                  Cliente confirmou sua chegada. Se habilitado, o sistema sugeriu mesas para
                                  alocação nesta etapa.
                                </p>
                              </div>
                              <div className='col-12'>
                                <div className='d-flex align-items-center gap-2 mb-2'>
                                  <span className='badge bg-danger'>✕</span>
                                  <strong className='text-dark'>Cancelada</strong>
                                </div>
                                <p className='text-muted small mb-0'>
                                  Reserva foi cancelada pelo cliente ou pelo sistema (expirou).
                                </p>
                              </div>
                              <div className='col-12'>
                                <div className='d-flex align-items-center gap-2 mb-2'>
                                  <span className='badge bg-warning text-dark'>⚠️</span>
                                  <strong className='text-dark'>Não Compareceu</strong>
                                </div>
                                <p className='text-muted small mb-0'>
                                  Cliente confirmou mas não chegou no horário. Reserva expirou.
                                </p>
                              </div>
                            </div>
                          </div>

                          <hr className='my-3' />

                          {/* Mesas Alocadas */}
                          <div className='mb-4'>
                            <h6 className='fw-bold text-dark mb-3'>
                              <i className='bi bi-question-circle me-2'></i>
                              Por que as Mesas Alocadas não aparecem?
                            </h6>
                            <div className='alert alert-warning p-2 small'>
                              <p className='mb-2'>
                                <strong>Motivos comuns:</strong>
                              </p>
                              <ul className='mb-0'>
                                <li>
                                  <strong>Reserva não confirmada:</strong> Mesas são alocadas SOMENTE após cliente
                                  confirmar presença. Enquanto está "Aguardando Confirmação", nenhuma mesa aparece.
                                </li>
                                <li>
                                  <strong>Gestão de mesas desabilitada:</strong> Se a funcionalidade não foi ativada
                                  nas configurações, as mesas não serão exibidas.
                                </li>
                                <li>
                                  <strong>Sem mesas alocadas:</strong> É possível confirmar uma reserva sem alocar
                                  mesas, deixando a escolha para o atendimento pessoal.
                                </li>
                              </ul>
                            </div>
                          </div>

                          {/* Estados das Mesas - Condicional */}
                          {enableTableManagement && (
                            <>
                              <hr className='my-3' />
                              <div className='mb-4'>
                                <h6 className='fw-bold text-dark mb-3'>
                                  <i className='bi bi-palette-fill me-2'></i>
                                  Estados e Cores das Mesas
                                </h6>
                                <div className='row g-3'>
                                  <div className='col-md-6'>
                                    <div className='d-flex align-items-center gap-2 mb-2'>
                                      <span className='badge bg-info'>
                                        <i className='bi bi-table me-1'></i> Mesa 5
                                      </span>
                                      <span className='text-muted small'>Alocada</span>
                                    </div>
                                    <p className='text-muted small mb-0'>
                                      Mesa foi reservada para o cliente. Disponível para ser ocupada.
                                    </p>
                                  </div>
                                  <div className='col-md-6'>
                                    <div className='d-flex align-items-center gap-2 mb-2'>
                                      <span className='badge bg-danger'>
                                        <i className='bi bi-table me-1'></i> Mesa 5
                                      </span>
                                      <span className='text-muted small'>Ocupada</span>
                                    </div>
                                    <p className='text-muted small mb-0'>
                                      Cliente está na mesa. Não pode ser liberada ou limpada.
                                    </p>
                                  </div>
                                  <div className='col-md-6'>
                                    <div className='d-flex align-items-center gap-2 mb-2'>
                                      <span className='badge bg-warning text-dark'>
                                        <i className='bi bi-table me-1'></i> Mesa 5
                                      </span>
                                      <span className='text-muted small'>Limpando</span>
                                    </div>
                                    <p className='text-muted small mb-0'>
                                      Mesa em processo de limpeza após cliente sair. Indisponível.
                                    </p>
                                  </div>
                                </div>
                              </div>

                              <hr className='my-3' />

                              <div className='mb-3'>
                                <h6 className='fw-bold text-dark mb-3'>
                                  <i className='bi bi-arrow-repeat me-2'></i>
                                  Como Alterar o Estado da Mesa
                                </h6>
                                <div className='alert alert-light p-2 mb-2'>
                                  <p className='mb-2 small'>
                                    <strong>1. Cliente Chegou?</strong>
                                    <br />
                                    Use o botão{' '}
                                    <span className='badge bg-danger text-white ms-1'>Ocupada</span> abaixo
                                    <br />
                                    <span className='text-muted'>
                                      Isso mostra que o cliente está presente na mesa.
                                    </span>
                                  </p>
                                </div>
                                <div className='alert alert-light p-2 mb-2'>
                                  <p className='mb-2 small'>
                                    <strong>2. Cliente Saiu?</strong>
                                    <br />
                                    Use o botão{' '}
                                    <span className='badge bg-warning text-dark ms-1'>Limpando</span> abaixo
                                    <br />
                                    <span className='text-muted'>
                                      Inicia o processo de limpeza automático (duração configurável).
                                    </span>
                                  </p>
                                </div>


                              </div>

                              <hr className='my-3' />

                              <div className='mb-3'>
                                <h6 className='fw-bold text-dark mb-2'>
                                  <i className='bi bi-exclamation-circle me-2'></i>
                                  Impactos ao Alterar Estados
                                </h6>
                                <ul className='small text-muted mb-0'>
                                  <li>
                                    <strong>Ocupada:</strong> A data de chegada é registrada. Não pode ser
                                    cancelada.
                                  </li>
                                  <li>
                                    <strong>Limpando:</strong> Mesa fica indisponível por X minutos (configurável).
                                  </li>
                                  <li>
                                    <strong>Alocada novamente:</strong> Após limpeza, volta a estar disponível.
                                  </li>
                                </ul>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </ModalWrapper>

          {/* Modal de Sugestão de Mesa */}
          <TableSuggestionModal
            show={showTableSuggestionModal}
            onClose={() => {
              setShowTableSuggestionModal(false)
              setBookingForAllocation(null)
            }}
            onAccept={(suggestion) => allocateTable(suggestion)}
            onConfirmWithoutTable={() => confirmBookingWithoutTable('confirmed')}
            suggestions={tableSuggestions}
          />
        </div>
      )}
    </>
  )
}

const BookingManagerDay: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: true},
          {title: 'Gerenciador de Reservas', path: 'booking/managerday', isActive: true},
        ]}
      >
        Gerenciador de Reservas
      </PageTitle>
      <BookingManagerDayContent />
    </>
  )
}

export {BookingManagerDay}
